import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import GeneralButton from "../common/GeneralButton";
import { TiChevronLeft, TiChevronRight, TiPlus } from "react-icons/ti";
import {
  useStep,
  useAddress,
  useMealType,
  useMealsSelection,
  useChildDetails,
  useDisclaimer,
  useKid,
  useDate,
} from "../contexts/SubscriptionContext";
import Icon from "../common/Icon";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import { Formik } from "formik";
import { addressSchema } from "../utils/validations";
import { useToast } from "../contexts/ToastContext";
import Modal from "../common/Modal";
import CMSFormSelect from "../formFields/CMSFormSelect";
import { useCart } from "../contexts/CartContext";
import { useNavigate, useParams } from "react-router-dom";
import ContainerLoading from "../common/loading/ContainerLoading";
import { useAuth } from "../contexts/AuthContext";
import {
  anyUnavailableDeliveryCities,
  CITY_OPTIONS,
  filterAvailableCityOptions,
  getMinimumMealCountByCity,
  getUnavailableDeliveryCities,
  isCityDeliverable,
  replaceWithCountAndCity,
  validateMealSelectionAndCityInCart,
} from "../utils/commonHelper";
import { useGeneral } from "../contexts/GeneralContext";

const Address = ({ onNext, onCitySelect }) => {
  const { type } = useParams();
  const { nextStep, prevStep } = useStep();
  const navigate = useNavigate();
  const { extraContent, fetchExtraContent } = useGeneral();
  const { discountCoupons } = useGeneral();
  const { setSelectedMealType } = useMealType();
  const { clearMeals } = useMealsSelection();
  const { clearChildDetails } = useChildDetails();
  const { resetDisclaimer } = useDisclaimer();
  const { setSelectedKidId } = useKid();
  const {
    loading,
    fetchAddresses,
    savedAddresses,
    selectedMealAddressId,
    addAddress,
    editAddress,
    removeAddress,
    selectAddressForMeal,
    selectAddress,
    selectedAddressId,
  } = useAddress();
  const { setSelectedDate } = useDate();
  const { cart, updateDeliveryChargeByCity, setSelectedCity, fetchCart } =
    useCart();
  const [state, setState] = useState(
    savedAddresses.length > 0 ? "list" : "add",
  );
  const { isLoggedIn } = useAuth();
  const [deletingAddress, setDeletingAddress] = useState(null);
  let user = localStorage.getItem("loggedInUser");
  const handleCloseModal = () => {
    setError("");
    setDeletingAddress(null);
  };

  const clearLocalStorage = () => {
    localStorage.removeItem("selectedPlanNew");
    localStorage.removeItem("selectedMealTypeNew");
    localStorage.removeItem("selectedChildNew");
    localStorage.removeItem("selectedMealsNew");
    localStorage.removeItem("childDetailsNew");
    localStorage.removeItem("acceptedDisclaimerNew");
    localStorage.removeItem("subscriptionStartDate");
    localStorage.removeItem("selectedDateNew");
    localStorage.removeItem("promoCodeSaved");
    localStorage.removeItem("selectedCity");
    localStorage.removeItem("mealCartId");
    setSelectedMealType(null);
    setSelectedDate(null);
    setSelectedKidId(null);
    clearMeals();
    clearChildDetails();
    resetDisclaimer();
  };
  if (user && user !== undefined && user !== "undefined") {
    user = JSON.parse(user);
  }
  let mealCartId = localStorage.getItem("mealCartId");
  let cartIdSaved = localStorage.getItem("cartId");
  const [show, setShow] = useState(false);
  const [showMealError, setShowMealError] = useState(false);
  const [showCityError, setShowCityError] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [error, setError] = useState("");
  const [editingAddress, setEditingAddress] = useState(null);
  const { addToast } = useToast();
  // If editing, populate form with existing data
  useEffect(() => {
    if (editingAddress) setState("add");
  }, [editingAddress]);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (cart?.customer && !hasFetched) {
        fetchAddresses(type);
        fetchExtraContent();
        setHasFetched(true);
      }
    }, 300);
  }, [cart?.customer, cart?.mealCart?._id, type, hasFetched]);
  useEffect(() => {
    setState(savedAddresses.length > 0 ? "list" : "add");
  }, [savedAddresses]);
  const isMealSessionExpired = () => {
    const sessionStarted = localStorage.getItem("sessionStarted");
    if (!sessionStarted) return false; // no session marker, nothing to expire

    const elapsedMs = Date.now() - new Date(sessionStarted).getTime();
    return elapsedMs > 24 *60 * 60 * 1000;
  };
  const handleSubmit = async (values, resetForm) => {
    try {
      let success = false;

      if (editingAddress) {
        success = await editAddress(editingAddress._id, values, type);

        if (success) {
          addToast("Address updated successfully", "success");
          setEditingAddress(null);
          await fetchCart();
        }
      } else {
        success = await addAddress(values, type);
        if (success) {
          addToast("Address added successfully", "success");
        }
      }

      if (success) {
        resetForm();
        setState("list");
      } else {
        setError("Failed to save address. Please try again.");
        setTimeout(() => {
          setError("");
        }, 5000);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setError("Failed to save address. Please try again.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };
  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    address: useRef(null),
    landmark: useRef(null),
    city: useRef(null),
    country: useRef(null),
    isDefault: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;

    // Smoothly scroll the nearest scrollable parent
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    // Give the scroll some time to finish, then focus
    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 500); // adjust based on your animation speed
  };
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    const autoSelect = async () => {
      if (hasAutoSelected.current) return; // ✅ Prevent repeat
      const id = type === "meals" ? selectedMealAddressId : selectedAddressId;
      if (!id || !savedAddresses?.length) return;

      const addr = savedAddresses.find((a) => a._id === id);
      if (!addr) return;

      try {
        let success = false;

        if (
          type === "meals" &&
          cart?.mealCart &&
          !cart?.mealCart?.shippingAddress?._id
        ) {
          const res = await selectAddressForMeal(addr._id, mealCartId);
          success = res?.success || false;
        }

        if (
          type === "order" &&
          cart?.orderCart &&
          !cart?.orderCart?.shippingAddress?._id
        ) {
          const res = await selectAddress(addr._id, cartIdSaved);
          success = res?.success || false;
          //updateDeliveryChargeByCity(addr.city);
          if (onCitySelect) onCitySelect();
        }

        if (success) {
          hasAutoSelected.current = true; // ✅ mark done
          //updateDeliveryChargeByCity(addr.city);
          if (onCitySelect) onCitySelect();
        } else {
          //console.error("Auto-select API failed");
        }
      } catch (err) {
        // console.error("Auto-select failed:", err);
      }
    };
    if (cart?.customer && savedAddresses.length > 0) {
      setTimeout(() => {
        autoSelect();
      }, 300);
    }
  }, [savedAddresses, selectedMealAddressId, selectedAddressId, type]);

  return (
    <motion.div
      initial={{ x: -200, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -200, opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-1 2xl:grid-cols-[1.19fr_1fr] gap-8 items-start ">
        <div>
          <div className="bg-[#DEE2DD]/40 w-full rounded-[15px] p-5 md:px-10 md:py-10 2.5xl:px-24 2.5xl:py-[70px] relative">
            <p className="text-green text-21pt">Delivery Address</p>
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-lg">
                <ContainerLoading />
              </div>
            )}
            {state === "list" && (
              <div className="grid grid-cols-1 gap-8 mt-8">
                {savedAddresses?.map((addr, index) => {
                  const id =
                    type === "meals"
                      ? selectedMealAddressId
                      : selectedAddressId;
                  const isSelected = id === addr._id;

                  return (
                    <div
                      key={index}
                      title="Click to select address"
                      className={`${
                        type === "meals" ? "bg-gray" : ""
                      } transition-transform duration-300 ease-in-out
                      hover:scale-[1.02] hover:shadow-lg py-3 grid md:grid-cols-[0.5fr_3.5fr_1fr] lg:grid-cols-[0.25fr_3.5fr_1fr] 2xl:grid-cols-[0.4fr_3.5fr_1fr] bg-white min-h-[120px] rounded-[15px] px-8 py-4 items-center cursor-pointer hover:shadow-md transition`}
                      onClick={async () => {
                        try {
                          let success = false;

                          if (type === "meals") {
                            const res = await selectAddressForMeal(
                              addr._id,
                              mealCartId,
                            );
                            success = res; // assuming your API returns { success: true }
                            //await fetchCart();
                          } else {
                            const res = await selectAddress(
                              addr._id,
                              cartIdSaved,
                            );

                            success = res;
                          }

                          // ✅ Only update delivery and trigger callback if API succeeded
                          if (success) {
                            // updateDeliveryChargeByCity(addr.city);
                            setTimeout(() => {
                              if (onCitySelect) onCitySelect();
                            }, 300);
                          } else {
                            console.error(
                              "API call failed, delivery not updated",
                            );
                          }
                        } catch (err) {
                          console.error("Failed to select address:", err);
                        }
                      }}
                    >
                      <div className="flex items-center justify-start mb-3 md:mb-2">
                        <Icon
                          icon={isSelected ? "radioSelected" : "radio"}
                          className="w-[27px] h-[32px]"
                        />
                      </div>

                      <div className="flex flex-col items-start justify-center mb-5 md:mb-0">
                        <p className="text-21pt leading-none">
                          {addr.firstName} {addr.lastName}
                        </p>
                        <p className="text-21pt leading-none mt-2">
                          {[addr.addressLine1, addr.area, addr.landmark]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        <p className="text-18pt font-mono leading-tight mt-2">
                          {[addr.city, addr.country].filter(Boolean).join(", ")}
                        </p>
                        <p className="text-16pt leading-tight text-green mt-1">
                          {[addr.email, addr.mobile].filter(Boolean).join(", ")}
                        </p>
                      </div>

                      <div className="flex items-center justify-end gap-8">
                        <Icon
                          icon="mapCross"
                          title="Click to remove address"
                          className="w-[27px] h-[32px] text-green hover:text-secondary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingAddress(addr._id);
                          }}
                        />
                        <Icon
                          icon="edit"
                          title="Click to update address"
                          className="w-[32px] h-[30px] text-green hover:text-secondary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingAddress(addr);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="grid gap-2">
                  {type === "meals" &&
                    !isCityDeliverable(cart, extraContent) &&
                    anyUnavailableDeliveryCities(extraContent) && (
                      <div className="w-100 bg-orange/10 border-l-4 border-orange p-2 mt-0 rounded-md shadow-sm flex items-center gap-4">
                        <motion.span
                          animate={{
                            rotate: [0, -10, 10, -10, 0],
                            scale: [1, 1.15, 1.05, 1.15, 1],
                          }}
                          transition={{
                            duration: 1.4,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="text-orange text-xl"
                        >
                          ⚠️
                        </motion.span>

                        <p className="text-orange text-16pt font-mono">
                          {extraContent?.mealUndeliverableCityMessages?.message}
                        </p>
                      </div>
                    )}
                  {type === "meals" &&
                    isCityDeliverable(cart, extraContent) &&
                    !validateMealSelectionAndCityInCart(cart, extraContent) && (
                      <div className="w-100 bg-orange/10 border-l-4 border-orange p-2 mt-0 rounded-md shadow-sm flex items-center gap-4">
                        <motion.span
                          animate={{
                            rotate: [0, -10, 10, -10, 0],
                            scale: [1, 1.15, 1.05, 1.15, 1],
                          }}
                          transition={{
                            duration: 1.4,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="text-orange text-xl"
                        >
                          ⚠️
                        </motion.span>

                        <p className="text-orange text-16pt font-mono">
                          {replaceWithCountAndCity(
                            cart,
                            extraContent,
                            extraContent?.minimumMealRequiredMessages
                              ?.modalMessage,
                          )}
                        </p>
                      </div>
                    )}
                </div>
                <GeneralButton
                  onClick={() => {
                    setEditingAddress(null);
                    setState("add");
                  }}
                  text="Add New Address"
                  title="Click to add new address"
                  bgColor="#fff"
                  textColor="#295B24"
                  icon={<TiPlus size={24} className="ms-1" />}
                  className="text-24pt hover:text-secondary brown"
                  hover="brown"
                />
              </div>
            )}
            <Modal
              isOpen={deletingAddress != null}
              onClose={handleCloseModal}
              title="Remove Address"
              onConfirm={async () => {
                try {
                  const success = await removeAddress(deletingAddress, type);

                  if (success) {
                    handleCloseModal();
                    fetchCart();
                    addToast("Address removed successfully", "success");
                    return;
                  }

                  // If removeAddress returned false (rare case)
                  setError("Failed to remove address. Please try again.");
                } catch (err) {
                  // 🔥 Catch axios errors, including 403
                  const status = err?.response?.status;

                  if (status === 403) {
                    setError(
                      err?.response?.data?.message ||
                        "Sorry, this address can’t be deleted as it’s currently linked to a subscription.",
                    );
                  } else {
                    setError("Failed to remove address. Please try again.");
                  }
                } finally {
                  // Auto-clear error after 5 seconds
                  setTimeout(() => {
                    setError("");
                  }, 5000);
                }
              }}
            >
              <div className="font-mono">
                <p className="mt-2 font-medium">
                  Are you sure you want to remove this address?
                </p>

                {error && (
                  <p
                    className={`relative transition-all duration-300 ease-in-out w-full min-h-[35px] xl:min-h-[20px]   flex items-center justify-center text-center mb-4 ${
                      error
                        ? "opacity-100 translate-y-3"
                        : "opacity-0 -translate-y-2"
                    }`}
                  >
                    {error && (
                      <span className="errorMessage2 w-full text-center">
                        {error}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </Modal>

            {state === "add" && (
              <div className="grid grid-cols-1 gap-12 mt-8">
                <Formik
                  initialValues={{
                    firstName:
                      editingAddress?.firstName || user?.firstName || "",
                    lastName: editingAddress?.lastName || user?.lastName || "",
                    email: editingAddress?.email || user?.email || "",
                    mobile: editingAddress?.mobile || user?.mobile || "",
                    address: editingAddress?.addressLine1 || "",
                    landmark: editingAddress?.landmark || "",
                    area: editingAddress?.area || "",
                    city: editingAddress?.city
                      ? editingAddress?.city
                      : type === "meals"
                        ? ""
                        : "",
                    country: editingAddress?.country || "UAE",
                    isDefault: editingAddress?.isDefault || "0",
                  }}
                  validationSchema={addressSchema}
                  onSubmit={(values, { resetForm }) => {
                    handleSubmit(values, resetForm);
                  }}
                >
                  {(form) => (
                    <div className="w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div ref={refs.firstName}>
                          <CMSFormInput
                            name="firstName"
                            label="Receiver's First Name"
                            form={form}
                            required
                          />
                        </div>
                        <div ref={refs.lastName}>
                          <CMSFormInput
                            name="lastName"
                            label="Receiver's Last Name"
                            form={form}
                            required
                          />
                        </div>

                        <div ref={refs.email}>
                          <CMSFormInput
                            name="email"
                            label="Receiver's Email Address"
                            form={form}
                            required
                          />
                        </div>
                        <div ref={refs.mobile}>
                          <CMSFormTelephone
                            name="mobile"
                            label="Mobile"
                            smallDescription="(with WhatsApp enabled)"
                            form={form}
                            required
                          />
                        </div>

                        <div ref={refs.address}>
                          <CMSFormInput
                            name="address"
                            label="Receiver's Full Address"
                            form={form}
                            required
                          />
                        </div>
                        <div ref={refs.area}>
                          <CMSFormInput
                            name="area"
                            label="Receiver's Area"
                            form={form}
                            required
                          />
                        </div>
                        <div ref={refs.landmark}>
                          <CMSFormInput
                            name="landmark"
                            label="Receiver's Landmark"
                            form={form}
                          />
                        </div>

                        <div ref={refs.city}>
                          <CMSFormSelect
                            name="city"
                            label="Receiver's City"
                            form={form}
                            selected={form.values.city}
                            // disabled={type === "meals" ? true : false}
                            onChange={(e) => {
                              form.handleChange(e);

                              // setSelectedCity(e.target.value)
                              //  localStorage.setItem(
                              //   "selectedCity",
                              //   JSON.stringify(e.target.value)
                              // );
                              //updateDeliveryChargeByCity(e.target.value);
                              //if (onCitySelect) onCitySelect();
                              const updated = {
                                ...form.values,
                                city: e.target.value,
                              };
                              localStorage.setItem(
                                "expressCheckoutAddress",
                                JSON.stringify(updated),
                              );
                            }}
                            placeholder={{ label: "Select City", value: "" }}
                            options={
                              type === "meals"
                                ? filterAvailableCityOptions(
                                    CITY_OPTIONS,
                                    extraContent,
                                  )
                                : CITY_OPTIONS
                            }
                            required
                          />
                        </div>
                        <div ref={refs.country}>
                          <CMSFormInput
                            name="country"
                            label="Receiver's Country"
                            form={form}
                            disabled
                            required
                          />
                        </div>
                      </div>

                      <div
                        ref={refs.isDefault}
                        className="flex items-center justify-start mt-10 cursor-pointer"
                        onClick={() =>
                          form.setFieldValue(
                            "isDefault",
                            form.values.isDefault === "1" ? "0" : "1",
                          )
                        }
                      >
                        <div className="w-[38px] h-[26px] me-5 flex items-center justify-center">
                          <Icon
                            icon={
                              form.values.isDefault === "1"
                                ? "checked"
                                : "checkbox"
                            }
                            className="w-[30px] h-auto"
                          />
                        </div>
                        <p className="text-green text-18pt md:text-21pt select-none">
                          Save as default address
                        </p>
                      </div>
                      {error && (
                        <div
                          className={`relative h-[20px] transition-all duration-300 ease-in-out my-3 ${
                            error
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-2"
                          }`}
                        >
                          {error && (
                            <span className="errorMessage">{error}</span>
                          )}
                        </div>
                      )}

                      <div className="flex flex-col-reverse md:flex-row items-center justify-between mt-10 gap-5">
                        {savedAddresses.length > 0 ? (
                          <GeneralButton
                            onClick={() => {
                              setEditingAddress(null);
                              setState("list");
                            }}
                            text="Saved Addresses"
                            title="Click to go back to address listing"
                            bgColor="#fff"
                            iconPosition="Left"
                            textColor="#295B24"
                            icon={<TiChevronLeft size={24} />}
                            className="text-24pt hover:text-secondary brown"
                            hover="brown"
                          />
                        ) : (
                          <div></div>
                        )}

                        <GeneralButton
                          onClick={() => {
                            form.handleSubmit();
                            setTimeout(
                              () => scrollToFirstError(form?.errors),
                              100,
                            );
                          }}
                          text={
                            editingAddress ? "Update Address" : "Add Address"
                          }
                          title="Click to add address"
                          type={"submit"}
                          iconPosition="Right"
                          textColor="#fff"
                          icon={<TiPlus size={24} />}
                          className="text-24pt"
                        />
                      </div>
                    </div>
                  )}
                </Formik>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col xs:flex-row gap-5 items-center justify-between mt-8 lg:mt-10">
            <GeneralButton
              onClick={() => {
                if (type === "meals" && isMealSessionExpired()) {
                  setShowSessionExpired(true);
                  clearLocalStorage();
                  return;
                } else {
                  navigate(-1);
                }

                //updateDeliveryChargeByCity("");
              }}
              text="Prev Step"
              title=""
              iconPosition="Left"
              bgColor="#ccc"
              textColor="#000"
              icon={<TiChevronLeft size={24} />}
              className="text-24pt"
            />
            <GeneralButton
              onClick={() => {
                if (type === "meals" && isMealSessionExpired()) {
                  setShowSessionExpired(true);
                  clearLocalStorage();
                  return;
                }

                if (
                  type === "meals" &&
                  selectedMealAddressId &&
                  !isCityDeliverable(cart, extraContent)
                ) {
                  setShowCityError(true);
                } else if (
                  type === "meals" &&
                  selectedMealAddressId &&
                  !validateMealSelectionAndCityInCart(cart, extraContent)
                ) {
                  setShowMealError(true);
                } else if (
                  type === "meals" ? selectedMealAddressId : selectedAddressId
                ) {
                  onNext ? onNext() : nextStep();
                } else {
                  setShow(true);
                }
              }}
              text="Next Step"
              title=""
              bgColor="#30160E"
              textColor="#ccc"
              icon={<TiChevronRight size={24} />}
              className="text-24pt"
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={show}
        onClose={() => setShow(false)}
        title="Select an Address"
        onConfirm={() => setShow(false)}
        confirmButton="Ok"
        dismissible={false}
        showCancel={false}
      >
        <div className="font-mono">
          <p>
            Please select an existing address or add a new address to continue.
          </p>
        </div>
      </Modal>
      <Modal
        isOpen={showMealError}
        onClose={() => setShowMealError(false)}
        title={replaceWithCountAndCity(
          cart,
          extraContent,
          extraContent?.minimumMealRequiredMessages?.modalAlert,
        )}
        onConfirm={() => setShowMealError(false)}
        confirmButton="Ok"
        dismissible={false}
        showCancel={false}
      >
        <div className="font-mono">
          <p>
            {replaceWithCountAndCity(
              cart,
              extraContent,
              extraContent?.minimumMealRequiredMessages?.modalMessage,
            )}
          </p>
        </div>
      </Modal>
      <Modal
        isOpen={showSessionExpired}
        onClose={() => setShowSessionExpired(false)}
        title="Session Expired"
        onConfirm={() => {
          setShowSessionExpired(false);
          // Clear stale session data and send the user back to restart the flow
          localStorage.removeItem("sessionStarted");

          navigate("/plans", { replace: true }); // adjust to your actual start-of-flow route
        }}
        confirmButton="Start Again"
        dismissible={false}
        showCancel={false}
      >
        <div className="font-mono">
          <p>
            Your session is no longer active. Please start the process again.
          </p>
        </div>
      </Modal>
      <Modal
        isOpen={showCityError}
        onClose={() => setShowCityError(false)}
        title={extraContent?.mealUndeliverableCityMessages?.modalAlert}
        onConfirm={() => setShowCityError(false)}
        confirmButton="Ok"
        dismissible={false}
        showCancel={false}
      >
        <div className="font-mono">
          <p>{extraContent?.mealUndeliverableCityMessages?.modalMessage}</p>
        </div>
      </Modal>
    </motion.div>
  );
};

export default Address;
